import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Lock, Eye, EyeOff, ArrowRight, Loader2, CheckCircle, AlertCircle, Zap } from 'lucide-react';
import { authApi } from '../lib/api';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setSubmitting(true);
    try {
      await authApi.resetPassword(token, password);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password. The link may have expired.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <main className="min-h-screen bg-black flex items-center justify-center px-4 py-20" data-testid="reset-no-token">
        <div className="text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
          <h1 className="text-white text-2xl mb-2" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Invalid Reset Link</h1>
          <p className="text-zinc-500 text-sm mb-6">This password reset link is missing or invalid.</p>
          <Link to="/auth" className="text-[#e8ff00] text-sm hover:underline">Go to Sign In</Link>
        </div>
      </main>
    );
  }

  if (success) {
    return (
      <main className="min-h-screen bg-black flex items-center justify-center px-4 py-20" data-testid="reset-success">
        <div className="text-center max-w-md">
          <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
          <h1 className="text-white text-2xl mb-2" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Password Reset!</h1>
          <p className="text-zinc-400 text-sm mb-6">Your password has been updated. You can now sign in with your new password.</p>
          <Link to="/auth" className="inline-flex items-center gap-2 bg-[#e8ff00] text-black px-6 py-3 text-sm uppercase tracking-wider hover:opacity-90 transition-opacity" style={{ fontFamily: 'Bebas Neue, sans-serif' }} data-testid="reset-login-btn">
            Sign In <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-black flex items-center justify-center px-4 py-20" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-10">
          <Zap className="w-5 h-5 text-[#e8ff00]" />
          <span className="text-white text-lg tracking-widest uppercase" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>NinjAthlete</span>
        </Link>

        <h2 className="text-white text-2xl mb-2" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Set New Password</h2>
        <p className="text-zinc-500 text-sm mb-6">Enter your new password below.</p>

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm px-4 py-3 mb-6" data-testid="reset-error">{error}</div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5" data-testid="reset-form">
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input
              type={showPass ? 'text' : 'password'}
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              placeholder="New password"
              minLength={6}
              className="w-full pl-10 pr-12 py-3 bg-zinc-900 border border-zinc-700 text-white text-sm placeholder-zinc-500 focus:border-[#e8ff00] focus:outline-none transition-colors"
              data-testid="reset-password"
            />
            <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 transition-colors">
              {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>

          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input
              type={showPass ? 'text' : 'password'}
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              required
              placeholder="Confirm new password"
              minLength={6}
              className="w-full pl-10 pr-4 py-3 bg-zinc-900 border border-zinc-700 text-white text-sm placeholder-zinc-500 focus:border-[#e8ff00] focus:outline-none transition-colors"
              data-testid="reset-confirm-password"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full py-3 bg-[#e8ff00] text-black text-sm uppercase tracking-wider hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
            style={{ fontFamily: 'Bebas Neue, sans-serif' }}
            data-testid="reset-submit-btn"
          >
            {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Resetting...</> : <>Reset Password <ArrowRight className="w-4 h-4" /></>}
          </button>
        </form>
      </div>
    </main>
  );
}
